<template>
  <b-container>
    <Headline :title="playlist.type === 'my-videos' ? 'Saved Videos' : playlist.name">
        <b-dropdown size="sm" no-caret right v-if="playlist.type === 'playlist' && isLoggedIn">
          <template slot="button-content">
            <font-awesome-icon icon="ellipsis-h" class="padded"></font-awesome-icon>
          </template>
          <b-dropdown-item v-if="isMyPlaylist" :to="{name: 'fan-playlist-edit', params: { id: playlist.id }}">Edit playlist</b-dropdown-item>
          <b-dropdown-item v-if="isMyPlaylist" v-b-modal.deleteModal>Delete playlist</b-dropdown-item>
          <b-dropdown-item v-if="!isMyPlaylist && !isFollowing" @click="follow">Follow playlist</b-dropdown-item>
          <b-dropdown-item v-if="!isMyPlaylist && isFollowing" @click="unfollow">Unfollow playlist</b-dropdown-item>
        </b-dropdown>

        <b-button v-if="playlist.type === 'my-videos'"
                  class="btn-secondary"
                  :to="{name: 'fan-playlist-edit', params: { id: playlist.id }}"
                  size="sm">
          edit
        </b-button>
    </Headline>

    <b-row class="subheadline">
      <b-col>
        <small>
          <span v-if="playlist.profile">
            Created by
              <router-link v-if="isMyPlaylist" :to="{name: 'fan-profile', params: { id: currentUser.id }}">you</router-link>
              <router-link v-else :to="{name: 'fan-profile', params: { id: playlist.profile.id }}">{{ displayName(playlist.profile) }}</router-link>
              &mdash;
          </span>
          <span v-if="playlist.videos">
            {{ numVideos }},
            {{ playlistDuration }}
            &mdash;
          </span>
          <span>
            {{ numFollowers }}
          </span>
        </small>
      </b-col>
    </b-row>

    <b-row>
      <b-col v-if="video" class="player-col">
        <Player v-model="video"
                :videos="videos"
                :position.sync="position"
                :autoplay="playlist.type !== 'my-videos'"
                :pageUrl="$route.fullPath"></Player>
      </b-col>
      <b-col v-else class="empty">
        <span v-if="isMyPlaylist">
          You have not added any videos yet
        </span>
        <span v-else>
          No videos
        </span>
      </b-col>
    </b-row>

    <VideoInfo :video="video" v-if="video"  />

    <div v-if="playlist.type === 'playlist'">
      <b-row v-if="related.length">
        <b-col>
          <h4>More playlists</h4>
        </b-col>
      </b-row>

      <PlaylistList :items="related" v-if="related.length" :exclude="playlist" />
    </div>
    <div v-else>
      <b-row v-if="videos.length">
        <b-col>
          <h4>Videos</h4>
        </b-col>
      </b-row>

      <div class="videos" v-if="videos">
        <VideoCard :video="video" v-for="video in videos" :key="video.id" :showDate="true" :showArtist="true" />
      </div>
    </div>

    <b-modal id="deleteModal"
             ref="deleteModal"
             title="Delete Playlist"
             @ok="deletePlaylist"
             centered>
      <div class="content">
        <p>
          Do you really want to delete this playlist?
        </p>
      </div>
    </b-modal>

  </b-container>
</template>

<style scoped lang="scss">
  @import '@/assets/custom.scss';
  @import '~bootstrap/scss/mixins/_breakpoints.scss';

  .container {
    margin-top: 1em;
    max-width: unset;
  }

  .headline {
    margin-bottom: 1em;
    border-bottom: 1px solid $warm-grey;

    /deep/ button {
      padding: 0 1em;
      border-radius: 4px !important;
    }
  }

  .subheadline {
    margin-bottom: 1em;
  }

  .empty {
    text-align: center;
    margin: 1em;
    color: $steel;
  }

  .videos {
    margin-top: 1em;
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    @include media-breakpoint-down(xs) {
      margin-top: 0;
      justify-content: space-between;
    }
  }

  @include media-breakpoint-up(md) {
    .videos {
      justify-content: left;
    }
  }

</style>

<script>
import { mapGetters } from 'vuex'
import sumBy from 'lodash/sumBy'

import FontAwesomeIcon from '@fortawesome/vue-fontawesome'
import Player from '@/components/widgets/Player'
import PlaylistList from '@/components/widgets/PlaylistList'
import VideoInfo from '@/components/widgets/VideoInfo'
import Headline from '@/components/widgets/Headline'
import VideoCard from '@/components/widgets/VideoCard'

import { songHelpers, profileHelpers } from '@/store/helpers'

export default {
  name: 'PlaylistDetail',
  components: {
    FontAwesomeIcon,
    Player,
    PlaylistList,
    VideoInfo,
    Headline,
    VideoCard
  },
  data () {
    return {
      playlist: {},
      related: [],
      position: 0,
      video: null
    }
  },
  computed: {
    videos () {
      if (!this.playlist) return []
      return this.playlist.videos || []
    },
    numVideos () {
      if (this.videos.length === 0) return 'no videos'
      if (this.videos.length === 1) return '1 video'
      return this.videos.length + ' videos'
    },
    numFollowers () {
      if (!this.playlist.is_public) return 'private'
      if (!this.playlist.follower_count) return 'no followers'
      if (this.playlist.follower_count === 1) return '1 follower'
      return this.playlist.follower_count + ' followers'
    },
    playlistDuration () {
      const d = sumBy(this.videos, v => v.duration)
      const h = Math.floor(d / 3600)
      const m = Math.floor(d / 60) % 60
      const s = d % 60

      let txt = s + 's'
      if (m) txt = m + 'min ' + txt
      if (h) txt = h + 'h ' + txt
      return txt
    },
    isMyPlaylist () {
      if (!this.isLoggedIn) return false
      return this.playlist.profile && (this.currentUser.id === this.playlist.profile.id)
    },
    isFollowing () {
      if (!this.isLoggedIn) return false
      return this.currentUser.followed_playlists.indexOf(this.playlist.id) !== -1
    },
    ...mapGetters('auth', ['isLoggedIn']),
    ...mapGetters('profile', ['currentUser'])
  },
  created () {
    this.fetchData()
  },
  watch: {
    '$route': 'fetchData',
    'position': 'updateQueryString'
  },
  methods: {
    async fetchData () {
      const params = this.$route.params
      const query = this.$route.query

      if (this.playlist && this.playlist.id === +params.id) return

      this.playlist = await this.$store.dispatch('playlist/getPlaylist', {id: params.id})
      this.position = +query.pos || 0
      this.video = (this.videos.length > 0) ? this.videos[this.position % this.videos.length] : null

      const filter = query.related ? { related: query.related } : { profile_id: this.playlist.profile.id }
      const relatedResponse = await this.$store.dispatch('playlist/listPlaylists', filter)

      // If we have related playlist IDs, we want all the results, otherwise just a first batch is fine
      this.related = query.related ? await relatedResponse.all() : relatedResponse.results
    },
    async follow () {
      this.$store.dispatch('playlist/followPlaylist', this.playlist.id)
    },
    async unfollow () {
      this.$store.dispatch('playlist/unfollowPlaylist', this.playlist.id)
    },
    async deletePlaylist () {
      await this.$store.dispatch('playlist/deletePlaylist', this.playlist.id)
      this.$store.dispatch('flash/showMessage', { message: 'Playlist deleted' })
      this.$router.push({name: 'fan-playlists-list'})
    },
    updateQueryString () {
      const route = this.$route

      if (this.position === +route.query.pos) return

      const query = { ...route.query }
      query.pos = this.position

      this.$router.push({
        name: route.name,
        meta: route.meta,
        params: { id: this.playlist.id },
        query: query
      })
    },
    ...songHelpers,
    ...profileHelpers
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/fan/PlaylistDetail.vue